import React, { useState, useEffect } from "react";
import {
  getFirestore,
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { Spinner, Button, Form, Modal, Table } from "react-bootstrap";
import { BasicColor } from "../../Utils/constants";

const VideoGalleryAdmin = ({ divTitle }) => {
  const { t, i18n } = useTranslation();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentVideoId, setCurrentVideoId] = useState(null);
  const [form, setForm] = useState({
    title: "",
    subtitle: "",
    url: "",
    order: 0,
  });

  const db = getFirestore();

  // Fetch videos from Firestore
  const fetchVideos = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, "ytVideos"));
      const fetchedVideos = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort videos by the "order" field in ascending order
      const sortedVideos = fetchedVideos.sort((a, b) => a.order - b.order);

      setVideos(sortedVideos);
    } catch (error) {
      console.error("Error fetching videos:", error);
    }
    setLoading(false);
  };
  useEffect(() => {
    fetchVideos();
  }, [db]);

  // Handle input change for form fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Add or Update Video
  const handleSaveVideo = async () => {
    try {
      if (editMode) {
        // Update video
        const videoDocRef = doc(db, "ytVideos", currentVideoId);
        await updateDoc(videoDocRef, form);
        alert("Video updated successfully!");
      } else {
        // Add new video
        await addDoc(collection(db, "ytVideos"), form);
        alert("Video added successfully!");
      }
      setForm({ title: "", subtitle: "", url: "", order: 0 });
      setEditMode(false);
      setCurrentVideoId(null);
      setShowModal(false);
      fetchVideos();
    } catch (error) {
      console.error("Error saving video:", error);
      alert("Failed to save video. Please try again.");
    }
  };

  // Edit Video (Populate form with existing video data)
  const handleEditVideo = (video) => {
    setForm({
      title: video.title,
      subtitle: video.subtitle,
      url: video.url,
      order: video.order,
    });
    setEditMode(true);
    setCurrentVideoId(video.id);
    setShowModal(true);
  };

  // Delete Video
  const handleDeleteVideo = async (videoId) => {
    if (window.confirm("Are you sure you want to delete this video?")) {
      try {
        const videoDocRef = doc(db, "ytVideos", videoId);
        await deleteDoc(videoDocRef);
        alert("Video deleted successfully!");
        setVideos((prev) => prev.filter((video) => video.id !== videoId));
      } catch (error) {
        console.error("Error deleting video:", error);
        alert("Failed to delete video. Please try again.");
      }
    }
  };

  // Open Modal for Adding New Video
  const handleAddVideo = () => {
    setForm({ title: "", subtitle: "", url: "", order: 0 });
    setEditMode(false);
    setShowModal(true);
  };

  return (
    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
      <div
        style={{
          textAlign: `${i18n.dir() === "ltr" ? "left" : "right"}`,
          fontWeight: "bold",
          color: BasicColor,
        }}
      >
        <h4>{divTitle}</h4>
      </div>

      <Button onClick={handleAddVideo} style={{ marginBottom: "10px" }}>
        Add New Video
      </Button>

      {loading ? (
        <Spinner animation="border" className="d-block mx-auto" />
      ) : (
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>Order</th>
              <th>Title</th>
              <th>Subtitle</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {videos.map((video, index) => (
              <tr key={video.id}>
                <td>{video.order}</td>
                <td>{video.title}</td>
                <td>{video.subtitle}</td>
                <td>
                  <Button
                    variant="warning"
                    onClick={() => handleEditVideo(video)}
                    size="sm"
                  >
                    Edit
                  </Button>{" "}
                  <Button
                    variant="danger"
                    onClick={() => handleDeleteVideo(video.id)}
                    size="sm"
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editMode ? "Edit Video" : "Add New Video"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={form.title}
                onChange={handleInputChange}
                placeholder="Enter video title"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Subtitle</Form.Label>
              <Form.Control
                type="text"
                name="subtitle"
                value={form.subtitle}
                onChange={handleInputChange}
                placeholder="Enter video subtitle"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Video URL</Form.Label>
              <Form.Control
                type="text"
                name="url"
                value={form.url}
                onChange={handleInputChange}
                placeholder="Enter video URL"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Order</Form.Label>
              <Form.Control
                type="number"
                name="order"
                value={form.order}
                onChange={handleInputChange}
                placeholder="Enter video order"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSaveVideo}>
            {editMode ? "Update Video" : "Add Video"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VideoGalleryAdmin;
